import type { DrawContext } from './draw';
import type { View } from './project';
import type { Random } from './rng';
import { toScreen } from './project';
import { drawOpenFan } from './open-fan';
import type { FanChrome, FanDrawn, FanFog, FanNode, FanLimits } from './open-fan';
import { drawHoverFan } from './hover-fan';
import { drawReaderChips } from './reader-chips';
import type { ChipZooms, ReadersAt } from './reader-chips';
import type { LabelSpace } from './labels';
import type { MapNode, ShownNode } from './nodes';

/*
 * WHAT MOVES — the layers drawn fresh every frame, over the cached still picture.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawFrame` (1716–1984). The layers are being
 * taken a few at a time; `V8-PORT.md` lists all ten with their line ranges and which are done.
 *
 * THIS IS THE HALF THAT MAKES THE OTHER HALF WORTH CACHING. `drawStatic` paints ten thousand names,
 * a web, a wash and a star field into an offscreen canvas. This blits that image and then draws
 * only the things that actually change between one frame and the next — a few dozen marks. Our old
 * renderer had no such split: it redrew everything, every frame, and that is the architectural
 * reason it ran at a fifth of the frame rate rather than any one slow layer.
 *
 * A FRAME IS A PURE FUNCTION OF ITS TIME. Theirs reads `performance.now()` in the middle of the
 * method; ours takes the seconds as an argument. That is the only way a frame is testable, and the
 * only way this can be driven from a worker, where there is no shared clock to agree about.
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/**
 * BLIT THE CACHED STILL PICTURE.
 *
 * `setTransform(1,0,0,1,0,0)` first, because the offscreen canvas is already in DEVICE pixels — it
 * was drawn at `dpr` and must be copied one to one, not scaled again. Then back to `dpr` so
 * everything after it is in CSS pixels like the rest of the engine.
 *
 * That pair of calls is the whole cache. Everything `drawStatic` did arrives in one `drawImage`.
 */
export function blitStatic(ctx: DrawContext, still: unknown, dpr: number): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.drawImage(still, 0, 0);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/**
 * One reader on their way somewhere.
 *
 * `t` runs 0 to 1 along the tie from `a` to `b`, so a traveller is a position on an edge rather
 * than a position in the world — which is what makes them read as going somewhere rather than as
 * drifting.
 */
export interface Traveller {
  /** The two names they are between, by index. */
  a: number;
  b: number;
  /** How far along, 0 to 1. */
  t: number;
  /** Their colour. */
  c: string;
}

/** A name, as the moving layers need it. */
export interface LiveNode {
  x: number;
  y: number;
  /** Its index into the fog sets, and what makes its animation phase its own. */
  id?: number;
  /** Its radius in world units. */
  r?: number;
  /** How many readers are here. Zero when the presence plane is not connected. */
  pop?: number;
  /** Whether one of them has a live voice. */
  voice?: boolean;
  /** Whether the traditions disagree about who this is. */
  disputed?: boolean;
  /** What kind of thing it is — an unagreed name gets no pulse. */
  kind?: string;
  /** The scripture that names it most, which colours the pulse. */
  dom?: string;
}

/**
 * The zooms these layers switch on at — arguments, because a zoom is pixels per world unit.
 *
 * Their 0.14 is the same number the resident web uses: below it the names are dots, and a reader
 * moving between two dots is a dot moving between dots.
 */
export interface LiveZooms {
  travellersFrom: number;
  /** Where the seats around an occupied name appear. Theirs is 0.62. */
  presenceFrom: number;
  /** Where a disputed name begins to pulse. Theirs is the same 0.14 as the travellers. */
  disputedFrom: number;
  /** Where the near dust starts to show, and over how much zoom it comes fully in. */
  nearFrom: number;
  nearSpan: number;
}

/** How far off screen a traveller is still drawn. */
const TRAVELLER_MARGIN = 10;

/** How long the tail behind them is, as a share of the whole tie. */
const TAIL_SHARE = 0.03;

/** How big the head is, and how solid. */
const HEAD_R = 1.6;
const HEAD_ALPHA = 0.85;

/** The tail's own alpha, as the two hex digits their colours carry. */
const TAIL_ALPHA = '55';

/**
 * Draw the readers in transit. Returns how many were drawn.
 *
 * Each is a short tail and a head — the tail pointing back the way they came, so the mark reads as
 * a direction rather than as a dot. Drawn in the frame layer rather than the still one because they
 * move every frame and nothing else about the tie does.
 */
export function drawTravellers(
  ctx: DrawContext,
  view: View,
  nodes: readonly LiveNode[],
  travellers: readonly Traveller[],
  zooms: LiveZooms,
): number {
  const z = view.cam.z;
  if (z <= zooms.travellersFrom) return 0;
  const W = view.view.width;
  const H = view.view.height;
  let drawn = 0;
  for (const tr of travellers) {
    const a = nodes[tr.a];
    const b = nodes[tr.b];
    if (a === undefined || b === undefined) continue;
    const wx = a.x + (b.x - a.x) * tr.t;
    const wy = a.y + (b.y - a.y) * tr.t;
    const p = toScreen(view, wx, wy);
    if (p.x < -TRAVELLER_MARGIN || p.x > W + TRAVELLER_MARGIN) continue;
    if (p.y < -TRAVELLER_MARGIN || p.y > H + TRAVELLER_MARGIN) continue;
    /* The tail is measured in WORLD units back along the tie, so it foreshortens with everything
       else rather than staying a fixed number of pixels long. */
    const back = toScreen(view, wx - (b.x - a.x) * TAIL_SHARE, wy - (b.y - a.y) * TAIL_SHARE);
    ctx.strokeStyle = tr.c + TAIL_ALPHA;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(back.x, back.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    ctx.fillStyle = tr.c;
    ctx.globalAlpha = HEAD_ALPHA;
    ctx.beginPath();
    ctx.arc(p.x, p.y, HEAD_R, 0, TAU);
    ctx.fill();
    ctx.globalAlpha = 1;
    drawn += 1;
  }
  return drawn;
}

/*
 * ── WHO IS HERE ──────────────────────────────────────────────────────────────────────────────────
 *
 * Ported from `drawFrame` lines 1740–1759.
 *
 * Readers sitting at a name are drawn as seats around it — one small dot each, turning slowly, and a
 * ring pulsing outward when somebody has a live voice. It is the only layer on the map that says
 * anything about other people, and it is drawn per frame because it moves.
 *
 * TWO THINGS ARE DELIBERATELY QUIET ABOUT IT. It appears only when the reader is right in close, so
 * nobody is watched from across the field; and a name they have never been near shows nothing at
 * all, because `known` gates it. Presence is something you find by going somewhere, not a heat map.
 *
 * EACH NAME'S RING TURNS FROM ITS OWN ID. `t*0.05 + n.id` gives every occupied name a different
 * phase, so a screen full of them does not pulse in unison — which would read as one thing
 * happening rather than several.
 */

/** The most seats one name shows, however many readers are there. */
export interface PresenceWorld {
  /**
   * How many readers a name can show before it stops counting.
   *
   * DATA: it turns a POPULATION into a visual, and how many readers a busy name has is a fact about
   * our deployment rather than about their method. Theirs is eight.
   */
  seatCap: number;
}

/** How far out the seats orbit, as the name's radius plus this. */
const ORBIT_MIN = 5.5;
const ORBIT_SHARE = 0.78;

/** How big a seat is. */
const SEAT_MIN = 1;
const SEAT_MAX = 1.7;
const SEAT_SHARE = 0.1;

/** How fast the ring of seats turns, in radians a second. Look, not data. */
const SEAT_RATE = 0.05;

/** How far past the seats a voice ring travels before it fades out, and how fast. */
const VOICE_REACH = 24;
const VOICE_RATE = 0.42;
const VOICE_STAGGER = 0.13;
const VOICE_ALPHA = 0.42;
const VOICE_WIDTH = 1.1;

/** How far off screen a name is still worth seats. */
const PRESENCE_MARGIN = 60;

/** The colour of everything to do with other people. */
const PRESENCE_COLOUR = '99,224,232';

/** The smallest and largest a name is drawn, shared with `nodes.ts`. */
const RADIUS_MIN = 1.1;
const RADIUS_MAX = 46;

/**
 * Draw who is here. Returns how many names showed anybody.
 *
 * `known` is passed in rather than derived: whether the reader has been near a name is the trail's
 * business, and this layer only asks.
 */
export function drawPresence(
  ctx: DrawContext,
  view: View,
  nodes: readonly LiveNode[],
  world: PresenceWorld,
  zooms: LiveZooms,
  seconds: number,
  known: (node: LiveNode) => boolean,
): number {
  const z = view.cam.z;
  if (z <= zooms.presenceFrom) return 0;
  const W = view.view.width;
  const H = view.view.height;
  let shown = 0;
  for (const n of nodes) {
    if (!n.pop || !known(n)) continue;
    const p = toScreen(view, n.x, n.y);
    const r = Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, (n.r ?? 0) * z));
    if (p.x < -PRESENCE_MARGIN || p.x > W + PRESENCE_MARGIN) continue;
    if (p.y < -PRESENCE_MARGIN || p.y > H + PRESENCE_MARGIN) continue;
    const seats = Math.min(world.seatCap, n.pop);
    const step = TAU / seats;
    const orbit = r + Math.max(ORBIT_MIN, r * ORBIT_SHARE);
    const dot = Math.max(SEAT_MIN, Math.min(SEAT_MAX, r * SEAT_SHARE));
    ctx.fillStyle = `rgba(${PRESENCE_COLOUR},.5)`;
    for (let i = 0; i < seats; i++) {
      /* The name's own id offsets the phase, so a screen of occupied names does not pulse as one. */
      const angle = i * step + seconds * SEAT_RATE + (n.id ?? 0);
      ctx.beginPath();
      ctx.arc(p.x + Math.cos(angle) * orbit, p.y + Math.sin(angle) * orbit, dot, 0, TAU);
      ctx.fill();
    }
    if (n.voice === true) {
      /* One ring, travelling outward and fading as it goes — a heartbeat rather than a highlight. */
      const phase = (seconds * VOICE_RATE + (n.id ?? 0) * VOICE_STAGGER) % 1;
      ctx.strokeStyle = `rgba(${PRESENCE_COLOUR},${((1 - phase) * VOICE_ALPHA).toFixed(3)})`;
      ctx.lineWidth = VOICE_WIDTH;
      ctx.beginPath();
      ctx.arc(p.x, p.y, orbit + phase * VOICE_REACH, 0, TAU);
      ctx.stroke();
    }
    shown += 1;
  }
  return shown;
}

/*
 * ── WHERE THE TRADITIONS DO NOT AGREE ────────────────────────────────────────────────────────────
 *
 * Ported from `drawFrame` lines 1761–1778.
 *
 * A slow pulse around a name whose identity is argued about — the Cush who is a person in one
 * reading and a place in another, the Mary who may be two Marys. It is the map's own subject matter
 * made visible: the whole project is about friction between traditions, and this is the one mark
 * that says "there is an argument here" without opening anything.
 *
 * DRAWN IN THE NAME'S OWN COLOUR, not in a warning colour. A disputed identity is not an error, and
 * the mark should not read as one — it reads as the name breathing.
 *
 * AND IT IS UNDER THE FOG like everything else: a name never approached pulses at a sixth strength.
 * The reader is shown that something is there, not what it is.
 */

/** How much of the pulse a name gets, by how near the reader has been. */
const DISPUTED_WALKED = 1;
const DISPUTED_SEEN = 0.5;
const DISPUTED_UNSEEN = 0.16;

/** How fast it breathes, and how far apart two names' breaths are. */
const PULSE_RATE = 1.25;
const PULSE_STAGGER = 0.7;

/** How far the pulse reaches, as a multiple of the name's radius, and how strong it is. */
const PULSE_INNER = 0.6;
const PULSE_SPAN = 1.5;
const PULSE_PAD = 3;
const PULSE_FLOOR = 0.08;
const PULSE_RANGE = 0.34;
/** Where the gradient turns over, and the smallest a name may be. */
const PULSE_MID = 0.45;
const DISPUTED_RADIUS_MIN = 1.2;

/** How far off screen a disputed name still pulses. */
const DISPUTED_MARGIN = 70;

/** The colour a name falls back to when no scripture claims it. */
const UNCLAIMED = '#c9d2e4';

/**
 * Draw the arguments. Returns how many names pulsed.
 *
 * `absentKind` is the kind that is drawn as an outline rather than a disc — a thing not everybody
 * agrees exists. Theirs excludes it, and rightly: a pulse saying "the traditions disagree" around a
 * mark that already says "this may not be a thing" is the same claim twice.
 */
export function drawDisputed(
  ctx: DrawContext,
  view: View,
  nodes: readonly LiveNode[],
  palette: Readonly<Record<string, string>>,
  zooms: LiveZooms,
  seconds: number,
  fog: { visited: ReadonlySet<number>; seen: ReadonlySet<number> },
  absentKind: string,
): number {
  const z = view.cam.z;
  if (z <= zooms.disputedFrom) return 0;
  const W = view.view.width;
  const H = view.view.height;
  let pulsed = 0;
  for (const n of nodes) {
    if (n.disputed !== true || n.kind === absentKind) continue;
    const id = n.id ?? 0;
    const lit = fog.visited.has(id) ? DISPUTED_WALKED : fog.seen.has(id) ? DISPUTED_SEEN : DISPUTED_UNSEEN;
    const p = toScreen(view, n.x, n.y);
    const r = Math.max(DISPUTED_RADIUS_MIN, Math.min(RADIUS_MAX, (n.r ?? 0) * z));
    if (p.x < -DISPUTED_MARGIN || p.x > W + DISPUTED_MARGIN) continue;
    if (p.y < -DISPUTED_MARGIN || p.y > H + DISPUTED_MARGIN) continue;
    const phase = 0.5 + 0.5 * Math.sin(seconds * PULSE_RATE + id * PULSE_STAGGER);
    const reach = r * PULSE_SPAN + r * PULSE_SPAN * phase + PULSE_PAD;
    const g = ctx.createRadialGradient(p.x, p.y, r * PULSE_INNER, p.x, p.y, reach);
    const colour = palette[n.dom ?? ''] ?? UNCLAIMED;
    g.addColorStop(0, colour);
    g.addColorStop(PULSE_MID, colour);
    g.addColorStop(1, `${colour}00`);
    ctx.globalAlpha = (PULSE_FLOOR + PULSE_RANGE * phase) * lit;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y, reach, 0, TAU);
    ctx.fill();
    ctx.globalAlpha = 1;
    pulsed += 1;
  }
  return pulsed;
}

/*
 * ── THE DUST IN FRONT OF THE READER ──────────────────────────────────────────────────────────────
 *
 * Ported from `drawFrame` lines 1780–1796.
 *
 * A SECOND DUST FIELD, AND IT IS NOT THE ONE IN `void.ts`. That one is out past the rim, sits in the
 * WORLD, and turns with it. This one lives in SCREEN space: seventy-four motes drifting across the
 * viewport, wrapping at the edges, each at its own depth. It is between the reader and the map
 * rather than behind it.
 *
 * WHICH IS WHY IT IS THE ONLY LAYER WITH REAL PARALLAX. Each mote moves against the camera by its
 * own depth — `cam.x * z * 0.2 * dp` — so a pan slides the near ones further than the far ones, and
 * the map reads as something the reader is moving THROUGH rather than a picture being dragged. It
 * appears only close in, because that is the only range where the field is near enough for the
 * illusion to mean anything.
 *
 * The wrap is a true modulo, not a remainder: `((v % m) + m) % m`. A mote drifting off the left edge
 * has a negative position, and JavaScript's `%` keeps the sign — which would put it nowhere.
 */

/** Their seed for this field. Arbitrary, and therefore mechanism: it must simply be the same one. */
export const NEAR_DUST_SEED = 4711;

/** How many motes. A density over the screen rather than over the world, so it is not data. */
export const NEAR_DUST_COUNT = 74;

/** One mote: where it starts, how fast it drifts, how deep it sits, how big and how bright. */
export interface Mote {
  /** Its starting place, as a fraction of the viewport. */
  fx: number;
  fy: number;
  /** Its depth, which sets the parallax, the size and the brightness together. */
  dp: number;
  /** How fast it drifts, in screen pixels a second. */
  vx: number;
  vy: number;
  sz: number;
  br: number;
}

/** How much the camera moves a mote, per unit of depth. */
const PARALLAX = 0.2;

/** How big a mote is drawn, by its depth. */
const SIZE_FLOOR = 0.7;
const SIZE_PER_DEPTH = 0.6;

/** The colour of the motes. Look. */
const MOTE_COLOUR = '#dce7fa';

/** Below this much of the fade the layer is not worth a draw call. */
const NEAR_DUST_MIN = 0.02;

/** Build the field once. Seeded, so it is the same seventy-four motes every session. */
export function buildNearDust(random: Random, count = NEAR_DUST_COUNT): Mote[] {
  const motes: Mote[] = [];
  for (let i = 0; i < count; i++) {
    motes.push({
      fx: random(),
      fy: random(),
      dp: 0.35 + random() * 0.85,
      vx: (random() - 0.5) * 5.5,
      vy: (random() - 0.5) * 4,
      sz: 0.55 + random() * 1.15,
      br: 0.16 + random() * 0.5,
    });
  }
  return motes;
}

/**
 * How much of the near field is showing: nothing until the reader is close, all of it closer still.
 *
 * The zooms are arguments, because a zoom is pixels per world unit.
 */
export function nearFade(zoom: number, zooms: LiveZooms): number {
  return Math.min(1, Math.max(0, (zoom - zooms.nearFrom) / zooms.nearSpan));
}

/** A true modulo. `%` keeps the sign, which would put a mote drifting off the left edge nowhere. */
function wrap(v: number, m: number): number {
  return ((v % m) + m) % m;
}

/** Draw the motes. Returns how many were drawn. */
export function drawNearDust(
  ctx: DrawContext,
  view: View,
  motes: readonly Mote[],
  zooms: LiveZooms,
  seconds: number,
): number {
  const fade = nearFade(view.cam.z, zooms);
  if (fade <= NEAR_DUST_MIN) return 0;
  const W = view.view.width;
  const H = view.view.height;
  const z = view.cam.z;
  for (const p of motes) {
    /* Its own drift, minus the camera's own movement scaled by how near it is. */
    const px = wrap(p.fx * W + p.vx * seconds - view.cam.x * z * PARALLAX * p.dp, W);
    const py = wrap(p.fy * H + p.vy * seconds - view.cam.y * z * PARALLAX * p.dp, H);
    ctx.globalAlpha = p.br * fade * p.dp;
    ctx.fillStyle = MOTE_COLOUR;
    ctx.beginPath();
    ctx.arc(px, py, p.sz * (SIZE_FLOOR + p.dp * SIZE_PER_DEPTH), 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  return motes.length;
}

/*
 * ── THE COMET ────────────────────────────────────────────────────────────────────────────────────
 *
 * Ported from `drawFrame` lines 1798–1826.
 *
 * One streak crossing the sky every few seconds, in from the left edge or the top, fading in over
 * half a second and out over the last two of its sixteen. It is the only thing on the map that
 * happens without the reader doing anything, and it is what makes a still screen feel alive rather
 * than paused.
 *
 * THE ONE UNSEEDED THING IN THIS ENGINE, AND THE ONE DEVIATION IN THIS FILE. Theirs calls
 * `Math.random()` four times — for when the next one is due, which edge it enters by, where along
 * that edge, and how fast and at what angle. That cannot be compared against anything, which would
 * make this the only layer in the port taken on trust. So it takes a `Random`, the same
 * `mulberry32` the dust and the stars already use: a caller who wants an unpredictable comet seeds
 * it from the clock, and a test seeds it from a number. Nothing about the arithmetic changes.
 *
 * IT IS STATE, NOT A FUNCTION OF TIME. A comet that appeared purely from the clock would restart
 * whenever the tab did; theirs remembers where it is and when the next is due. `CometState` holds
 * exactly what theirs holds on the component.
 */

/** A comet in flight, or the memory of when the next one is due. */
export interface CometState {
  /** The one in flight, if any. */
  flying?: {
    x0: number;
    y0: number;
    vx: number;
    vy: number;
    /** When it started, in the same seconds the frame is drawn at. */
    t0: number;
  };
  /** When the next one is due. Zero means "as soon as one is asked for". */
  next: number;
}

/** How long a comet lives before it gives up, and how long it fades in and out. */
const COMET_LIFE = 16;
const COMET_FADE_IN = 0.5;
const COMET_FADE_OUT = 2;

/** How far past the screen it travels before it is finished. */
const COMET_OVERRUN = 140;

/** How long until the next one, once there is none. */
const COMET_WAIT_MIN = 4;
const COMET_WAIT_SPAN = 10;
/** And once one has just finished, which is a longer rest. */
const COMET_REST_MIN = 8;
const COMET_REST_SPAN = 16;

/** Where it enters: off the left edge, or above the top. */
const COMET_LEFT_X = -80;
const COMET_TOP_Y = -70;
/** How far down the left edge it may enter — never the bottom third. */
const COMET_LEFT_SPAN = 0.7;

/** How fast, and at what angle. */
const COMET_SPEED_MIN = 170;
const COMET_SPEED_SPAN = 130;
const COMET_ANGLE_MIN = 0.5;
const COMET_ANGLE_SPAN = 0.55;

/** How long the tail is, how wide, and how bright. */
const COMET_TAIL = 54;
const COMET_WIDTH = 1.2;
const COMET_ALPHA = 0.5;
/** The head is brighter than the tail's own start. */
const COMET_HEAD_LIFT = 1.5;
const COMET_HEAD_R = 1.5;

/** The colours. Look. */
const COMET_TAIL_HEAD = '232,242,255';
const COMET_TAIL_END = 'rgba(200,216,244,0)';
const COMET_HEAD = '#f2f7ff';

/**
 * Advance the comet and draw it. Returns the state to carry into the next frame.
 *
 * Takes and returns the state rather than mutating a field, so a caller holding two views of the
 * same map does not have them fighting over one comet.
 */
export function drawComet(
  ctx: DrawContext,
  view: View,
  state: CometState,
  zooms: LiveZooms,
  seconds: number,
  random: Random,
): CometState {
  const fade = nearFade(view.cam.z, zooms);
  /* The same gate as the near dust: theirs draws both inside one `closeIn` block. */
  if (fade <= NEAR_DUST_MIN) return state;
  const W = view.view.width;
  const H = view.view.height;
  const flying = state.flying;

  if (flying === undefined) {
    /* None in flight. Wait until one is due, then decide where it comes from. */
    const next = state.next > 0 ? state.next : seconds + COMET_WAIT_MIN + random() * COMET_WAIT_SPAN;
    if (seconds <= next) return { next };
    const edge = random();
    const from = edge < 0.5
      ? { x: COMET_LEFT_X, y: H * random() * COMET_LEFT_SPAN }
      : { x: W * random(), y: COMET_TOP_Y };
    const speed = COMET_SPEED_MIN + random() * COMET_SPEED_SPAN;
    const angle = COMET_ANGLE_MIN + random() * COMET_ANGLE_SPAN;
    return {
      flying: { x0: from.x, y0: from.y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, t0: seconds },
      next: 0,
    };
  }

  const elapsed = seconds - flying.t0;
  const x = flying.x0 + flying.vx * elapsed;
  const y = flying.y0 + flying.vy * elapsed;
  if (x > W + COMET_OVERRUN || y > H + COMET_OVERRUN || elapsed > COMET_LIFE) {
    /* Off the far side, or out of time. A longer rest than the first wait. */
    return { next: seconds + COMET_REST_MIN + random() * COMET_REST_SPAN };
  }

  /* The tail points back along the way it came, a fixed length whatever the speed. */
  const ux = -flying.vx;
  const uy = -flying.vy;
  const m = Math.hypot(ux, uy) || 1;
  const ex = x + (ux / m) * COMET_TAIL;
  const ey = y + (uy / m) * COMET_TAIL;
  const fadeIn = Math.min(1, elapsed / COMET_FADE_IN);
  const fadeOut = Math.min(1, (COMET_LIFE - elapsed) / COMET_FADE_OUT);
  const alpha = COMET_ALPHA * fade * fadeIn * fadeOut;
  const g = ctx.createLinearGradient(x, y, ex, ey);
  g.addColorStop(0, `rgba(${COMET_TAIL_HEAD},${alpha.toFixed(3)})`);
  g.addColorStop(1, COMET_TAIL_END);
  ctx.strokeStyle = g;
  ctx.lineWidth = COMET_WIDTH;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.globalAlpha = alpha * COMET_HEAD_LIFT;
  ctx.fillStyle = COMET_HEAD;
  ctx.beginPath();
  ctx.arc(x, y, COMET_HEAD_R, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
  return state;
}

/*
 * ── THE TRAIL ────────────────────────────────────────────────────────────────────────────────────
 *
 * Ported from `drawFrame` lines 1976–1984.
 *
 * A DASHED LINE MARCHING FROM WHERE THE READER WAS TO WHERE THEY HAVE JUST GONE. It draws itself on
 * over nine tenths of a second, holds, and fades out — and it exists because a map that simply
 * arrives somewhere new has lost the reader. This says "you came from there", once, and goes.
 *
 * IT MARCHES BECAUSE THE DASH PATTERN MOVES, NOT THE LINE. `lineDashOffset` is the only property in
 * the whole engine that is animated rather than set, and it is what turns a dotted line into a
 * direction. The offset is negative, so the dashes travel FORWARD along the way the reader went.
 *
 * ITS CLOCK IS ITS OWN. `age` is how long this trail has existed, not the frame's seconds — the
 * caller advances it, the same shape as the comet's state, because a trail that read the wall clock
 * would be half over the moment it appeared.
 */

/** Where the reader came from and went to, and how old that is. */
export interface Trail {
  /** The two names, by index. */
  a: number;
  b: number;
  /** How long this trail has been showing, in seconds. */
  age: number;
}

/** How long the line takes to draw itself on. */
const TRAIL_DRAW = 0.9;

/** When it starts fading, and over how long. */
const TRAIL_HOLD = 1;
const TRAIL_FADE = 0.6;

/** The look: a warm line, and the pattern it marches in. */
const TRAIL_COLOUR = '217,164,65';
const TRAIL_ALPHA = 0.75;
const TRAIL_WIDTH = 1.6;
const TRAIL_DASH = [5, 6];

/** How fast the dashes travel, in pixels a second. */
const TRAIL_MARCH = 24;

/**
 * Draw the trail. Returns whether it drew.
 *
 * `seconds` is the frame clock, which only the marching reads; everything else reads the trail's
 * own `age`. Both are needed and they are not the same number.
 */
export function drawTrail(
  ctx: DrawContext,
  view: View,
  nodes: readonly LiveNode[],
  trail: Trail,
  seconds: number,
): boolean {
  const a = nodes[trail.a];
  const b = nodes[trail.b];
  /* Streamed, so an end may not have landed. Theirs would throw. */
  if (a === undefined || b === undefined) return false;
  const as = toScreen(view, a.x, a.y);
  const bs = toScreen(view, b.x, b.y);
  /*
   * How much of it has been drawn, and how much of it is left to see.
   *
   * Their `>` could be `>=` and nothing would change: at exactly `TRAIL_HOLD` the fade works out to
   * one either way, so the two branches meet. Recorded because a mutation of it can never be caught
   * and a reader will otherwise try.
   */
  const drawn = Math.min(1, trail.age / TRAIL_DRAW);
  const fade = trail.age > TRAIL_HOLD ? 1 - (trail.age - TRAIL_HOLD) / TRAIL_FADE : 1;
  ctx.strokeStyle = `rgba(${TRAIL_COLOUR},${(TRAIL_ALPHA * fade).toFixed(3)})`;
  ctx.lineWidth = TRAIL_WIDTH;
  ctx.setLineDash(TRAIL_DASH);
  ctx.lineDashOffset = -seconds * TRAIL_MARCH;
  ctx.beginPath();
  ctx.moveTo(as.x, as.y);
  ctx.lineTo(as.x + (bs.x - as.x) * drawn, as.y + (bs.y - as.y) * drawn);
  ctx.stroke();
  /* Put the dash back, because everything drawn after this expects a solid line. */
  ctx.setLineDash([]);
  return true;
}

/*
 * ── THE WHOLE MOVING FRAME ───────────────────────────────────────────────────────────────────────
 *
 * Ported from `drawFrame` (1716–1985) — the METHOD, now that all ten of its layers are.
 *
 * WHAT THIS ADDS OVER THE TEN IS THE ORDER, AND THE ORDER IS THE PICTURE. Everything here is drawn
 * over the cached still, and every layer paints on top of the ones before it. The travellers pass
 * behind the presence seats; the near dust drifts in front of both; the open name's fan is above the
 * dust because it is the answer to a question and the dust is atmosphere; the chips are above
 * everything because they are the only layer made of words. Get the order wrong and each layer is
 * still right while the frame is wrong — which is exactly the class of mistake a per-layer test
 * cannot catch, and the reason this is a unit of its own.
 *
 * IT IS ALSO WHERE A VALUE THE CALLER FORGOT TO THREAD SHOWS UP. Ten signatures take about sixty
 * arguments between them, and a frame that quietly passes zero seconds to one of them looks fine
 * until it is compared against the whole method at several moments.
 *
 * TWO THINGS THEIRS READS FROM THE WORLD AND OURS TAKES AS ARGUMENTS. `performance.now()` becomes
 * `seconds`, and the comet's `Math.random()` becomes a `Random` — both stated in the layers
 * themselves. Everything else is a straight assembly.
 */

/** What does not change between frames: the world's own numbers, and the reader's look at them. */
export interface LiveScene {
  zooms: LiveZooms;
  presence: PresenceWorld;
  /** Corpus key → colour, which the disputed pulse reads. */
  palette: Readonly<Record<string, string>>;
  /** The kind that never pulses, however disputed — theirs is `x`. */
  absentKind: string;
  /** The near dust, built once from a seeded generator. */
  dust: readonly Mote[];
  fan: { zooms: FanLimits };
  chips: { zooms: ChipZooms };
}

/** What changes every frame. */
export interface LiveFrame {
  view: View;
  dpr: number;
  /** The frame clock, in seconds. Theirs reads it from `performance.now()` in the middle. */
  seconds: number;
  /** The cached still picture, and what the blit copies. */
  still: unknown;
  /**
   * The resident roster, and everything the moving layers ask of it.
   *
   * `LiveNode & FanNode` rather than either alone: the fans need a radius they can rely on, and the
   * moving layers are happy without one. The intersection is what says a frame must supply both.
   */
  nodes: readonly (LiveNode & FanNode)[];
  /** Readers in transit. */
  travellers: readonly Traveller[];
  /** Where the reader has been. */
  visited: ReadonlySet<number>;
  seen: ReadonlySet<number>;
  weak?: ReadonlySet<string>;
  /** Where a tie is written, keyed `min:max`. */
  refs?: ReadonlyMap<string, string>;
  /** Which name is open and which is under the pointer. */
  focus: number | null;
  hover: number | null;
  /** The open and hovered names' own ties, by index. */
  edgesOf: (id: number) => readonly number[];
  /** How much of the fog is lifted at each name — the disputed pulse reads it. */
  fog: { visited: ReadonlySet<number>; seen: ReadonlySet<number> };
  /** The names already painted, which the chips must keep clear of. */
  shown: readonly ShownNode[];
  /** The roster as the chips need it, with its scriptures and its readers. */
  chipNodes: readonly MapNode[];
  readersAt: ReadersAt;
  /** The chips' own reservation space, fresh each frame, holding the frame's chrome. */
  chipSpace: LabelSpace;
  chrome: FanChrome;
  /** The comet's state, carried from the last frame, and the stream it decides from. */
  comet: CometState;
  random: Random;
  /** Where the reader has just come from, if that is still showing. */
  trail?: Trail;
}

/** What the moving frame drew, for the readout. */
export interface LiveResult {
  travellers: number;
  presence: number;
  disputed: number;
  dust: number;
  /** The comet's state to carry into the next frame — it is state, not a function of time. */
  comet: CometState;
  fan: FanDrawn;
  hover: number;
  chips: number;
  trail: boolean;
}

/**
 * Draw everything that moves, over the cached still.
 *
 * The ten layers in their exact order. Returns what each of them drew, and the comet's new state.
 */
export function drawLive(ctx: DrawContext, scene: LiveScene, frame: LiveFrame): LiveResult {
  const { view, seconds, nodes } = frame;
  blitStatic(ctx, frame.still, frame.dpr);
  const travellers = drawTravellers(ctx, view, nodes, frame.travellers, scene.zooms);
  /* Their `known`: walked OR merely glimpsed. Presence is found by going somewhere, so a name the
     reader has never been near shows nobody at all. */
  const known = (node: LiveNode): boolean => {
    const id = node.id ?? 0;
    return frame.visited.has(id) || frame.seen.has(id);
  };
  const presence = drawPresence(ctx, view, nodes, scene.presence, scene.zooms, seconds, known);
  const disputed = drawDisputed(ctx, view, nodes, scene.palette, scene.zooms, seconds, frame.fog, scene.absentKind);
  const dust = drawNearDust(ctx, view, scene.dust, scene.zooms, seconds);
  /* The comet shares the near dust's gate and is drawn inside it — theirs is one `closeIn` block. */
  const comet = drawComet(ctx, view, frame.comet, scene.zooms, seconds, frame.random);

  const fog: FanFog = {
    walked: id => frame.visited.has(id),
    seen: id => frame.seen.has(id),
    weak: frame.weak,
    refs: frame.refs,
  };
  const fan = frame.focus === null
    ? { strands: 0, bead: false, passages: 0 }
    : drawOpenFan(ctx, view, nodes, frame.focus, frame.edgesOf(frame.focus), fog,
      scene.fan.zooms, frame.chrome, seconds);
  const hover = frame.hover === null
    ? 0
    : drawHoverFan(ctx, view, nodes, frame.hover, frame.edgesOf(frame.hover), fog);
  const chips = drawReaderChips(ctx, view, frame.chipNodes, frame.shown, scene.palette, frame.readersAt,
    scene.chips.zooms, frame.chipSpace, { focus: frame.focus, walked: id => frame.visited.has(id) }, seconds);
  const trail = frame.trail === undefined ? false : drawTrail(ctx, view, nodes, frame.trail, seconds);

  return { travellers, presence, disputed, dust, comet, fan, hover, chips, trail };
}
